import { forwardRef } from 'react';
import classNames from 'classnames';
import { generatePath } from 'react-router-dom';
import queryString from 'query-string';

import { Link } from '@ui/Link';
import { useCatalogRouteMatch, useSearchParams } from '@utils/common';
import { catalog, catalogBrand, catalogCity } from '@legacyUrls';

import s from './CatalogTopLevelCategories.sss';

export type TopLevelCategory = {
  id: number
  name: string
  slug: string
  countOfProducts: number
};

type CatalogTopLevelCategoriesProps = {
  categories: TopLevelCategory[];
  className?: string;
};

export const CatalogTopLevelCategories = forwardRef<HTMLDivElement, CatalogTopLevelCategoriesProps>(
  ({
    categories,
    className,
  }, ref) => {
    const [searchParams] = useSearchParams();
    const match = useCatalogRouteMatch();

    if (!match) {
      return null;
    }

    // This is super stupid hack.
    // If we on root 'clothes' category - top categories are also root categories
    // and we supposed to use `baseCatalog` pattern
    // If we on root categories (like 'women', 'men' ... etc) - top categories are
    // not root, we supposed to use `catalog` pattern
    let pattern = catalog;
    if (match.params.namespace === 'clothes') {
      pattern = match.path.replace(':namespace', ':slug');
    } else if (!('slug' in match.params)) {
      if ('city' in match.params) {
        pattern = catalogCity;
      } else if ('brand' in match.params) {
        pattern = catalogBrand;
      }
    }

    return (
      <div ref={ref} className={classNames(s.root, className)}>
        {
          categories
            .filter( // Exclude current catalog
              ({ slug }) => (
                'slug' in match.params
                  ? slug !== match.params.slug
                  : slug !== match.params.namespace
              ),
            )
            .map(
              (category) => (
                <Link
                  key={category.id}
                  className={s.link}
                  theme="black"
                  to={{
                    // We use decodeURIComponent to prevent '/' replacement with '%2F'
                    // in slug by generatePath function
                    pathname: decodeURIComponent(
                      generatePath(
                        pattern,
                        { ...match.params, slug: category.slug },
                      ),
                    ),
                    search: queryString.stringify({
                      // Transform indexed filters to search params
                      // we don't need indexed filter url for sub category link
                      ...(
                        match.params.indexedFilter
                          ? queryString.parse(match.params.indexedFilter)
                          : {}
                      ),
                      ...searchParams,
                      saved_search_id: undefined,
                      last_viewed_product_id: undefined,
                    }),
                  }}
                >
                  {category.name}
                  <span className={s.counter}>{category.countOfProducts}</span>
                </Link>
              ),
            )
        }
      </div>
    );
  },
);

CatalogTopLevelCategories.displayName = 'CatalogTopLevelCategories';
